import {ChatInput} from '@github-ui/copilot-chat/components/ChatInput'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useCallback, useEffect, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {SkeletonBox} from '@primer/react'
import {ReposSelector} from '@github-ui/repos-selector'
import {RefSelector} from '@github-ui/ref-selector'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {Repository} from '@github-ui/current-repository'
import {useSearchParams} from '@github-ui/react-router'

const PLACEHOLDER_EXAMPLES = [
  // General code improvements
  'Find and fix bugs or anti-patterns',
  'Improve performance of data loading',
  'Find and refactor duplicated code',
  'Generate or update developer documentation',
  'Find 5 TODOs in the codebase and fix them',
  'Detect unused code or dead files in the codebase',
  'Identify and suggest improvements to slow or inefficient code',
  'Suggest more descriptive variable and function names',
  'Add missing error handling and input validation',
  'Convert inline styles to CSS classes or styled components',
  // App and website features
  'Add a light/dark mode switcher',
  'Add client-side form validation for email inputs',
  'Add keyboard shortcuts for common actions',
  'Add auto-save functionality to forms',
  'Create a print-friendly view for reports',
  'Add confirmation dialogs for destructive actions',
  'Implement undo/redo functionality for text editors',
  'Add drag and drop upload and progress meters to file inputs',
  'Implement read/unread status for notifications',
  'Add column sorting to data tables',
]

export interface DropdownRepository {
  id: number
  name: string // This will be nameWithOwner for display in ReposSelector
  nameOnly: string // This will be the original repository name (without owner)
  nameWithOwner: string
  owner: string
  enabled: boolean
  defaultBranch: string
  private: boolean
}

async function queryForRepos(query: string = ''): Promise<DropdownRepository[]> {
  // Always fetch with empty query to get a broader set of repositories,
  // then filter client-side to support filtering by nameWithOwner
  const response = await verifiedFetchJSON(`/_filter/repositories?q=`)
  if (response.ok) {
    let repos = (await response.json()).repositories.map(
      (repo: {
        id: number
        nameWithOwner: string
        name: string
        owner: string
        defaultBranch: string
        visibility: string
      }) => {
        return {
          nameWithOwner: repo.nameWithOwner,
          enabled: true,
          id: repo.id,
          name: repo.nameWithOwner,
          nameOnly: repo.name,
          owner: repo.owner,
          defaultBranch: repo.defaultBranch,
          private: repo.visibility !== 'public',
        }
      },
    )

    // Apply client-side filtering to search both repository name and nameWithOwner
    if (query.trim()) {
      const lowerQuery = query.toLowerCase()
      repos = repos.filter(
        (repo: DropdownRepository) =>
          repo.nameWithOwner.toLowerCase().includes(lowerQuery) || repo.nameOnly.toLowerCase().includes(lowerQuery),
      )
    }

    return repos
  }
  return []
}

export function NewTaskChatInput({
  handleUserSubmit,
  initialRepo,
  hideRepoSelector,
}: {
  handleUserSubmit: (content: string, selectedRepo: DropdownRepository | null, selectedBranch: string) => Promise<void>
  initialRepo?: Repository | null
  hideRepoSelector?: boolean
}) {
  // Use the chat manager to clear references when a new task is started
  const manager = useChatManager()
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)
  const [isLoadingRepos, setIsLoadingRepos] = useState(true)
  const [placeholder] = useState(() => PLACEHOLDER_EXAMPLES[Math.floor(Math.random() * PLACEHOLDER_EXAMPLES.length)])
  const isBranchSelectionEnabled = isFeatureEnabled('copilot_immersive_agent_branch_selection')
  const [searchParams] = useSearchParams()
  const branchParam = searchParams.get('branch')
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  const nwoParam = searchParams.get('repo')

  useEffect(() => {
    if (initialRepo) {
      setSelectedRepo({
        id: initialRepo.id,
        owner: initialRepo.ownerLogin,
        name: `${initialRepo.ownerLogin}/${initialRepo.name}`,
        nameOnly: initialRepo.name,
        nameWithOwner: `${initialRepo.ownerLogin}/${initialRepo.name}`,
        enabled: true,
        defaultBranch: initialRepo.defaultBranch,
        private: initialRepo.private,
      })
      setSelectedBranch(branchParam ?? initialRepo.defaultBranch)
      manager.clearCurrentReferences()
      setIsLoadingRepos(false)
    } else {
      // Fetch the user's repositories as fallback
      const fetchRepos = async () => {
        const repos = await queryForRepos()

        const firstRepo = repos[0]

        if (firstRepo === undefined) {
          setIsLoadingRepos(false)
          return
        }

        let repo = firstRepo
        if (nwoParam) {
          const foundRepo = repos.find(r => r.nameWithOwner === nwoParam)
          repo = foundRepo || firstRepo
        }

        setSelectedRepo(repo)
        setSelectedBranch(branchParam ?? repo.defaultBranch)
        setIsLoadingRepos(false)
      }

      fetchRepos()
    }
    // We only want this hook to run once since it triggers a fetch.
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = useCallback(
    async (content: string) => {
      return handleUserSubmit(content, selectedRepo, selectedBranch)
    },
    [handleUserSubmit, selectedRepo, selectedBranch],
  )

  return (
    <ChatInput
      hideAttachmentButton
      placeholder={placeholder}
      size="large"
      onSubmit={onSubmit}
      submitButtonLabel="Start task"
      hideShadow
      extraToolbarLeft={
        <div className="d-flex gap-2 flex-wrap">
          {isLoadingRepos ? (
            <>
              {/* The height should match the populated button height and width should roughly match. */}
              {/* eslint-disable-next-line primer-react/no-system-props */}
              <SkeletonBox height="32px" width="130px" />
            </>
          ) : hideRepoSelector ? null : (
            <ReposSelector
              repositoryLoader={queryForRepos}
              selectionVariant="single"
              selectAllOption={false}
              onSelect={(repo: DropdownRepository | undefined) => {
                if (!repo) return
                setSelectedRepo(repo)
                setSelectedBranch(repo.defaultBranch)
              }}
              currentSelection={selectedRepo || undefined}
              buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
              width="medium"
            />
          )}
          {isBranchSelectionEnabled &&
            (selectedRepo ? (
              <RefSelector
                // `cacheKey` is something consumers of this component usually get from a key derived from a current repository in the app payload
                // In our case, it's a static string since this page is not aware of any kind of current repository
                cacheKey="agent-sessions-ref-selector"
                canCreate={false}
                currentCommitish={selectedBranch}
                defaultBranch={selectedRepo.defaultBranch}
                owner={selectedRepo.owner}
                repo={selectedRepo.nameOnly}
                types={['branch']}
                selectedRefType="branch"
                closeOnSelect
                hideShowAll
                onSelectItem={ref => {
                  setSelectedBranch(ref)
                }}
              />
            ) : (
              <>
                {/* The height should match the populated button height and width should roughly match. */}
                {/* eslint-disable-next-line primer-react/no-system-props */}
                <SkeletonBox height="32px" width="100px" />
              </>
            ))}
        </div>
      }
      submitDisabled={!selectedRepo || !selectedBranch}
    />
  )
}

try{ NewTaskChatInput.displayName ||= 'NewTaskChatInput' } catch {}